import { z } from 'zod';
import {
  LetterheadAlignmentSchema,
  LetterheadDocumentSchema,
  LetterheadDocumentWarningSchema,
  LetterheadImageSchema,
  LetterheadTextFieldsSchema,
  type LetterheadSettings,
} from './index.js';

// ── En-têtes de cabinet NOMMÉS et MULTIPLES (2026-09-03) ──────────────────────
//
// POURQUOI CE MODULE EXISTE À CÔTÉ DE `LetterheadSettings` (et ne le remplace pas)
// --------------------------------------------------------------------------------
// Un cabinet n'avait qu'UN en-tête, anonyme, dans `cabinet_settings.letterhead_settings`
// (migration 148). Il en a désormais PLUSIEURS, chacun NOMMÉ (« Cabinet Paris »,
// « En-tête associé Dupont »), rangés dans la table `cabinet_letterheads` (migration 163).
//
// `LetterheadSettings` reste le contrat de RENDU — celui que lisent l'add-in Word
// (`applyLetterhead.ts`), le canvas web (`canvasLetterhead.ts`), la page de garde PDF du
// bordereau (`coverSettings.ts`) et son export Word. Ces quatre moteurs ne savent pas, et
// n'ont pas à savoir, qu'un cabinet a maintenant plusieurs en-têtes : on leur passe
// l'en-tête CHOISI, projeté dans la forme historique par {@link toLegacyLetterheadSettings}.
//
// C'est ce qui rend la bascule non destructrice : le nouveau modèle ajoute une IDENTITÉ
// (id + nom + note d'usage) et une COLLECTION au-dessus d'une forme de rendu inchangée.

/**
 * Plafond du nombre d'en-têtes par cabinet.
 *
 * Ce n'est pas une limite technique mais une limite de LISIBILITÉ : la liste est destinée
 * à être lue par un avocat dans un menu et par l'agent conversationnel dans un tool result.
 * Au-delà d'une vingtaine, « choisir le bon en-tête selon le contexte » n'est plus un choix,
 * c'est une recherche — et l'agent, qui reçoit la liste entière, paierait le contexte.
 */
export const MAX_LETTERHEADS_PER_CABINET = 20;

/** Longueur maximale d'un nom d'en-tête — miroir du `varchar` de la migration 163. */
export const LETTERHEAD_NAME_MAX_LENGTH = 80;

/** Longueur maximale de la note d'usage — miroir de la migration 163. */
export const LETTERHEAD_USAGE_NOTE_MAX_LENGTH = 280;

/**
 * Nom d'un en-tête. Non vide APRÈS trim : un nom fait uniquement d'espaces s'afficherait
 * comme une entrée vide dans le menu de choix et serait innommable par l'agent.
 */
export const LetterheadNameSchema = z
  .string()
  .trim()
  .min(1, 'Le nom de l’en-tête ne peut pas être vide')
  .max(LETTERHEAD_NAME_MAX_LENGTH);

/**
 * Note d'usage — « à quoi sert cet en-tête », en une phrase.
 *
 * Elle n'est PAS décorative : c'est le seul élément qui permette à l'agent conversationnel
 * de choisir entre deux en-têtes sans demander à l'avocat (« Cabinet Lyon » ne dit pas
 * qu'il sert aux dossiers portés devant le TJ de Lyon, la note le dit).
 */
export const LetterheadUsageNoteSchema = z.string().trim().max(LETTERHEAD_USAGE_NOTE_MAX_LENGTH);

/**
 * Une ligne de `cabinet_letterheads`, forme SERVEUR (elle porte `image.filePath`, une clé S3
 * privée qui ne sort jamais vers le client — voir {@link CabinetLetterheadSummarySchema}).
 *
 * `text` est LEGACY et en lecture seule, exactement comme sur `LetterheadSettings` : les
 * lignes reprises de `cabinet_settings.letterhead_settings` par la migration 163 peuvent en
 * porter, plus aucun écran n'en produit. `.default(null)` — et non `.optional()` — pour que
 * la SORTIE reste `LetterheadTextFields | null` et qu'aucun consommateur n'ait à gérer
 * `undefined` (même arbitrage que `LetterheadSettingsSchema`).
 */
export const CabinetLetterheadSchema = z.object({
  id: z.string().uuid(),
  name: LetterheadNameSchema,
  usageNote: LetterheadUsageNoteSchema.nullable().default(null),
  enabled: z.boolean(),
  /**
   * L'en-tête servi aux appelants qui n'en nomment aucun — c'est-à-dire TOUS les appelants
   * historiques (add-in Word, page de garde du bordereau, pose automatique au montage du
   * canvas). Exactement un par cabinet, garanti par un index unique partiel côté base.
   */
  isDefault: z.boolean(),
  image: LetterheadImageSchema.nullable().default(null),
  /**
   * Le PAPIER du cabinet : un Word dont Hector a vidé le texte et gardé l'en-tête, le pied de
   * page et les marges. `null` = cet en-tête n'a qu'un logo (ou rien). Quand l'en-tête par
   * défaut en porte un, le canvas et l'add-in Word partent de lui au lieu de poser `image`.
   */
  document: LetterheadDocumentSchema.nullable().default(null),
  text: LetterheadTextFieldsSchema.nullable().default(null),
  alignment: LetterheadAlignmentSchema,
  firstPageOnly: z.boolean(),
});

/**
 * Ce qu'un client (navigateur, add-in, agent) reçoit d'un en-tête.
 *
 * `image.filePath` en est ABSENT : c'est une clé S3 privée. Le client reçoit à la place
 * `imageUrl` (URL signée, courte durée) pour l'aperçu, et télécharge les OCTETS par la route
 * proxy même-origine — le seau OVH n'a aucune configuration CORS (mesuré 2026-08-17 :
 * préflight `403`), donc un `fetch` direct sur l'URL signée échoue depuis le navigateur.
 */
export const CabinetLetterheadSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  usageNote: z.string().nullable(),
  enabled: z.boolean(),
  isDefault: z.boolean(),
  alignment: LetterheadAlignmentSchema,
  firstPageOnly: z.boolean(),
  /** Un en-tête sans fichier déposé existe (il a été nommé), mais ne pose rien. */
  hasImage: z.boolean(),
  /** Largeur d'affichage du logo en millimètres ; `null` = taille native de l'image. */
  imageWidthMm: z.number().positive().nullable(),
  /** URL signée (1 h) pour l'APERÇU seulement ; `null` sans image déposée. */
  imageUrl: z.string().nullable(),
  /**
   * Cet en-tête porte-t-il un papier ? Les trois champs ont un défaut pour qu'un client plus
   * récent que le backend lise « pas de papier » pendant un déploiement, au lieu de rejeter la
   * réponse entière.
   */
  hasDocument: z.boolean().default(false),
  /** Nom du fichier Word déposé, tel que l'avocat le connaît ; `null` sans papier. */
  documentFileName: z.string().nullable().default(null),
  /** Ce que le dépôt a remarqué sans refuser le papier (voir `LetterheadDocumentWarningSchema`). */
  documentWarnings: z.array(LetterheadDocumentWarningSchema).default([]),
});

/** Réponse de `GET /api/cabinet-settings/:cabinetId/letterheads`. */
export const CabinetLetterheadListResponseSchema = z.object({
  letterheads: z.array(CabinetLetterheadSummarySchema),
});

/** Réponse des routes qui rendent UN en-tête (création, modification). */
export const CabinetLetterheadResponseSchema = z.object({
  letterhead: CabinetLetterheadSummarySchema,
});

/**
 * Champs texte du multipart de création (`POST …/letterheads`), à côté du fichier.
 *
 * La création EXIGE un nom : c'est tout l'objet de la feature. Un en-tête sans nom serait
 * inchoisissable — ni dans le menu de l'avocat, ni par l'agent.
 */
export const CabinetLetterheadCreateSchema = z.object({
  name: LetterheadNameSchema,
  usageNote: LetterheadUsageNoteSchema.optional(),
});

/**
 * PATCH d'un en-tête. Tous les champs sont facultatifs : c'est une fusion CHAMP PAR CHAMP,
 * contrairement au `letterhead_settings` historique qui remplaçait tout le JSONB — et qui
 * exigeait, pour cette raison, que le serveur rattache `image` et `text` à chaque écriture.
 *
 * `isDefault` n'accepte que `true` : on PROMEUT un en-tête, on ne « dé-promeut » jamais.
 * Retirer le défaut sans en désigner un autre laisserait le cabinet sans en-tête servi aux
 * appelants qui n'en nomment aucun (add-in Word, bordereau) — une panne silencieuse sur des
 * documents déposés au greffe. Pour changer de défaut, on promeut l'autre.
 *
 * `imageWidthMm` : un nombre fixe la largeur du logo en millimètres, `null` revient à la
 * taille native, la clé omise laisse la largeur stockée inchangée. Plafond 190 mm ≈ la
 * largeur d'une A4 (210 mm) moins un cheveu — au-delà le logo déborderait de la page.
 */
export const CabinetLetterheadPatchSchema = z.object({
  name: LetterheadNameSchema.optional(),
  usageNote: LetterheadUsageNoteSchema.nullable().optional(),
  enabled: z.boolean().optional(),
  alignment: LetterheadAlignmentSchema.optional(),
  firstPageOnly: z.boolean().optional(),
  imageWidthMm: z.number().finite().positive().max(190).nullable().optional(),
  isDefault: z.literal(true).optional(),
});

/**
 * Projette un en-tête nommé dans la forme de RENDU historique.
 *
 * C'est la pièce qui rend la migration non destructrice : les quatre moteurs de rendu
 * (add-in Word, canvas web, page de garde PDF, export Word du bordereau) continuent de
 * consommer `LetterheadSettings` sans une ligne de changement, et ne voient jamais ni le nom,
 * ni la note, ni la collection.
 *
 * @param letterhead L'en-tête choisi, ou `null` (aucun en-tête dans le cabinet).
 * @returns La forme historique, ou `null` — que les quatre moteurs traitent déjà comme
 *   « aucun en-tête posé », leur cas nominal depuis 2026-07-26.
 */
export function toLegacyLetterheadSettings(
  letterhead: CabinetLetterhead | null | undefined,
): LetterheadSettings | null {
  if (!letterhead) return null;
  return {
    enabled: letterhead.enabled,
    image: letterhead.image,
    text: letterhead.text,
    alignment: letterhead.alignment,
    firstPageOnly: letterhead.firstPageOnly,
  };
}

/**
 * Forme canonique d'un nom pour la COMPARAISON : sans casse, sans accents, espaces
 * normalisés.
 *
 * Pourquoi les accents : l'avocat nomme son en-tête « Cabinet Évry » dans l'écran, et le dit
 * « cabinet evry » à l'agent — ou l'inverse. Une comparaison littérale ferait échouer un
 * appel parfaitement légitime, et l'agent, faute de mieux, poserait l'en-tête par défaut :
 * le mauvais papier sur un acte déposé.
 *
 * NFD + retrait des diacritiques combinants, plutôt qu'une table de correspondance : la table
 * oublie toujours un caractère (œ, ç, ï…) et l'oubli ne se voit qu'en production.
 */
export function normalizeLetterheadName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/**
 * Issue de la résolution d'un en-tête par son nom — une VALEUR, jamais une exception.
 *
 * Les trois cas mènent à trois suites DIFFÉRENTES pour l'agent, et les confondre en un seul
 * `null` lui ferait dire n'importe quoi à l'avocat : « aucun en-tête ne s'appelle ainsi »
 * (proposer la liste), « plusieurs correspondent » (faire choisir), « trouvé » (poser).
 */
export type LetterheadNameResolution<T extends { readonly name: string } = CabinetLetterheadSummary> =
  | { readonly kind: 'found'; readonly letterhead: T }
  | { readonly kind: 'not-found'; readonly available: readonly string[] }
  | { readonly kind: 'ambiguous'; readonly matches: readonly string[] };

/**
 * Résout un en-tête par le nom que l'avocat (ou l'agent) a prononcé.
 *
 * Deux passes, dans cet ordre et pas l'autre :
 *  1. **égalité canonique** — « Cabinet Paris » trouve « cabinet paris » ;
 *  2. **sous-chaîne, seulement si la première n'a rien donné** — « Lyon » trouve
 *     « Cabinet Lyon ». La passe 2 ne peut pas passer avant : un cabinet qui possède
 *     « Paris » ET « Paris — associés » verrait « Paris » déclaré ambigu alors qu'il désigne
 *     exactement une entrée.
 *
 * Plusieurs correspondances en passe 2 rendent `ambiguous` plutôt qu'un choix arbitraire :
 * poser le mauvais papier à en-tête sur un acte déposé au greffe est un dégât que l'avocat ne
 * voit qu'après coup.
 *
 * Générique sur le NOM seul : le navigateur résout sur la forme client (route de liste), le
 * backend sur la ligne serveur (`open_canvas_document` et son `modele_de_page`). Une même règle
 * de correspondance des deux côtés, sans projeter l'une des formes dans l'autre.
 *
 * @param letterheads La collection du cabinet (forme client ou ligne serveur).
 * @param query Le nom prononcé. Vide ou blanc ⇒ `not-found` (il n'y a rien à résoudre).
 */
export function resolveLetterheadByName<T extends { readonly name: string } = CabinetLetterheadSummary>(
  letterheads: readonly T[],
  query: string,
): LetterheadNameResolution<T> {
  const names = letterheads.map((l) => l.name);
  const wanted = normalizeLetterheadName(query);
  if (wanted.length === 0) return { kind: 'not-found', available: names };

  // `noUncheckedIndexedAccess` : un accès indexé rend `T | undefined` même après un test de
  // longueur, que le compilateur ne relie pas à l'index. Le destructuring nommé, lui, est
  // rétréci par le `if` qui suit — et évite un `!` non typé.
  const [premierExact, ...autresExacts] = letterheads.filter(
    (l) => normalizeLetterheadName(l.name) === wanted,
  );
  if (premierExact && autresExacts.length === 0) return { kind: 'found', letterhead: premierExact };
  if (premierExact) {
    return { kind: 'ambiguous', matches: [premierExact, ...autresExacts].map((l) => l.name) };
  }

  const [premierPartiel, ...autresPartiels] = letterheads.filter((l) =>
    normalizeLetterheadName(l.name).includes(wanted),
  );
  if (premierPartiel && autresPartiels.length === 0) {
    return { kind: 'found', letterhead: premierPartiel };
  }
  if (premierPartiel) {
    return { kind: 'ambiguous', matches: [premierPartiel, ...autresPartiels].map((l) => l.name) };
  }

  return { kind: 'not-found', available: names };
}

/**
 * L'en-tête à servir quand aucun n'est nommé : le défaut du cabinet.
 *
 * Fallback sur la PREMIÈRE entrée si aucune n'est marquée par défaut. L'index unique partiel
 * de la migration 163 garantit qu'il n'y en a jamais deux, pas qu'il y en a toujours un — une
 * suppression du défaut laisse la collection sans marque tant que le serveur n'a pas promu
 * son successeur. Rendre `null` dans ce trou ferait sortir un acte sans en-tête, ce qui est
 * pire que d'en poser un.
 */
export function pickDefaultLetterhead<T extends { isDefault: boolean }>(
  letterheads: readonly T[],
): T | null {
  return letterheads.find((l) => l.isDefault) ?? letterheads[0] ?? null;
}

/**
 * Valide une ligne `cabinet_letterheads` relue en base, et n'en rend rien si elle est
 * inexploitable.
 *
 * Fail-soft, comme `parseActiveLetterheadSettings` côté bordereau : un réglage cosmétique
 * corrompu ne doit jamais faire échouer une génération d'acte. L'appelant décide s'il
 * journalise.
 */
export function parseCabinetLetterhead(raw: unknown): CabinetLetterhead | null {
  const parsed = CabinetLetterheadSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export type CabinetLetterhead = z.infer<typeof CabinetLetterheadSchema>;
export type CabinetLetterheadSummary = z.infer<typeof CabinetLetterheadSummarySchema>;
export type CabinetLetterheadListResponse = z.infer<typeof CabinetLetterheadListResponseSchema>;
export type CabinetLetterheadResponse = z.infer<typeof CabinetLetterheadResponseSchema>;
export type CabinetLetterheadCreate = z.infer<typeof CabinetLetterheadCreateSchema>;
export type CabinetLetterheadPatch = z.infer<typeof CabinetLetterheadPatchSchema>;
